import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar, Clock, Dumbbell } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatDate, formatTime, getCurrencyIcon, getAccessToken, getUserId } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ClassDetails {
  id: number;
  title: string;
  classDateTime: string;
  duration: number;
  instructor: string;
  spots: number;
  price: number;
  currency: string;
  recurrent: boolean;
  practiceId: string;
}

interface BookingSummaryProps {
  isOpen: boolean;
  onClose: () => void;
  classDetails: ClassDetails;
  onConfirm: (sessions: number) => void;
}

const BookingSummary = ({ isOpen, onClose, classDetails, onConfirm }: BookingSummaryProps) => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState("1");
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);

    try {
      // Construct the booking payload
      const bookingPayload = {
        classId: classDetails.id,
        clientId: getUserId(),
        sessions: Number(sessions),
        startDateTime: classDetails.classDateTime,
        practiceId: classDetails.practiceId,
      };

      // API call to post the booking
      const response = await fetch(`${import.meta.env.VITE_BOOKINGS_ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'Authorization': 'Bearer ' + getAccessToken()
        },
        body: JSON.stringify(bookingPayload),
      });

      await response.text();

      if (response.status === 200 || response.status === 201) {
        // Pass the booking sessions to the parent component
        onConfirm(Number(sessions));
      } else if (response.status === 400) {
        // Handle 400 Bad Request (e.g., class fully booked)
        toast({
          title: t('bookingSummary.bookingFailedTitle'),
          description: t('bookingSummary.bookingFullDescription'),
          duration: 5000,
          variant: "destructive",
        });
      } else {
        throw new Error('Failed to book the class');
      }
    } catch (error) {
      // Handle errors
      console.error('Error booking class:', error);
      toast({
        title: t('bookingSummary.bookingFailedTitle'),
        description: t('bookingSummary.bookingFailedDescription'),
        duration: 5000,
        variant: "destructive",
      });
    } finally {
      setSessions("1");
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(false);
    setSessions("1");
  }, [classDetails, isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('bookingSummary.title')}</DialogTitle>
          <DialogDescription>
            {t('bookingSummary.description')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <h3 className="font-semibold text-lg">{classDetails.title}</h3>
            <div className="flex items-center text-gray-600">
              <Calendar className="w-5 h-5 mr-2" />
              <span>{formatDate(classDetails.classDateTime)}</span>
            </div>
            <div className="flex items-center text-gray-600">
              <Clock className="w-5 h-5 mr-2" />
              <span>{formatTime(classDetails.classDateTime)} ({classDetails.duration} {t('bookingSummary.min')})</span>
            </div>
            <div className="flex items-center text-gray-600">
              <Dumbbell className="w-5 h-5 mr-2" />
              <span>{classDetails.instructor}</span>
            </div>
            <div className="flex items-center text-gray-600">
              {getCurrencyIcon({ currencyCode: classDetails.currency })}
              <span>{classDetails.price * Number(sessions)}</span>
            </div>
          </div>

          {
            classDetails.recurrent && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="sessions">{t('bookingSummary.sessions')}</Label>
                  <Select value={sessions} onValueChange={setSessions}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('bookingSummary.selectSessions')} />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(12)].map((_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {i + 1}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-gray-500">
                    {t('bookingSummary.sessionsDescription')}
                  </p>
                </div>
              </>
            )
          }

          <Alert variant="destructive" className="bg-orange-50">
            <AlertDescription>
              {t('bookingSummary.cancellationPolicy')}
            </AlertDescription>
          </Alert>

          <p className="text-sm text-gray-500 mt-4">
            {t('bookingSummary.agreeText')}{" "}
            <a
              href="/terms/"
              target="_blank"
              className="text-blue-600 hover:underline"
            >
              {t('bookingSummary.terms')}
            </a>{" "}
            {t('bookingSummary.and')}{' '}
            <a
              href="/privacy/"
              target="_blank"
              className="text-blue-600 hover:underline"
            >
              {t('bookingSummary.privacyPolicy')}
            </a>
            .
          </p>

          <Button onClick={handleConfirm} className="w-full" disabled={loading}>
            {loading ? t('common.pleaseWait') : t('bookingSummary.confirmBooking')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingSummary;